import { useCallback } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import QuestionForm, {
  QuestionFormSubmitValues,
} from "pages/QuestionsPage/QuestionForm";
import { IQuestion, SaveQuestion } from "services/KnowfullyAdminClient";
import { Mode } from "types/common-types";
import translateError from "utils/translateError";
import { isImportedQuestion } from "utils/evaluationQuestionUtils";

type QuestionEditDialogProps = {
  question: IQuestion;
  evaluationOrganizationId: string;
  onSave: (updatedQuestion: IQuestion) => void;
  onCancel: () => void;
};

const QuestionEditDialog = ({
  question,
  evaluationOrganizationId,
  onSave,
  onCancel,
}: QuestionEditDialogProps) => {
  const { formatMessage: t } = useIntl();
  const { questionService } = useGlobalContext();
  const queryClient = useQueryClient();

  const handleSubmit = useCallback(
    async (values: QuestionFormSubmitValues) => {
      try {
        const questionId = isImportedQuestion(question) ? undefined : question.id;
        const response = await questionService.saveQuestion(
          new SaveQuestion({
            ...values,
            id: questionId,
            organizationId: question.organizationId ?? evaluationOrganizationId,
          })
        );

        const savedId = response?.id ?? question.id;

        queryClient.invalidateQueries(["question", savedId]);
        queryClient.invalidateQueries("questions");

        const updatedQuestion: IQuestion = {
          ...question,
          id: savedId,
          organizationId: question.organizationId ?? evaluationOrganizationId,
          value: values.value,
          required: values.required,
          hasConfidenceLevels: values.hasConfidenceLevels,
          questionFeedback: values.questionFeedback,
          questionFeedbackId: values.questionFeedbackId,
          evaluationQuestionAnswers: values.answers,
        };

        onSave(updatedQuestion);
      } catch (error) {
        console.log(error);
        return { [FORM_ERROR]: translateError(error, t) };
      }
    },
    [question, evaluationOrganizationId, onSave, questionService, queryClient, t]
  );

  return (
    <QuestionForm
      question={question}
      submitLabel={t({ id: "questionFormUpdateButton" })}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      mode={Mode.Update}
    />
  );
};

export default QuestionEditDialog;
